import { FC, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { toRound } from '../../../../../utils/get-rounded-number.util';
import { VESTING_GRAPH_EVENT_BADGE_OVERFLOW_PX } from '../model/vesting-graph.constants';
import { formatVestingCalendarDate } from '../model/vesting-graph.format';
import { VestingGraphAxisTicks } from './vesting-graph-axis-ticks';
import { VestingGraphChart } from './vesting-graph-chart';
import {
  VestingGraphCliffBracket,
  VestingGraphTargetMilestoneBracket,
  VestingGraphTargetMilestoneColumn,
  VestingGraphTargetMilestoneColumnEdges,
} from './vesting-graph-milestones';
import { VestingGraphPlotMarkers } from './vesting-graph-plot-markers';
import { VestingGraphTooltip } from './vesting-graph-tooltip';
import { useVestingGraphContext } from './vesting-graph-view-context';

export const VestingGraphPlot: FC = () => {
  const { vestingGraph, view } = useVestingGraphContext();
  const {
    axisTicks,
    chartMinWidth,
    cliffOverlay,
    format,
    hoveredPeriod,
    introDelayForReveal,
    introPhase,
    isPlotInteractive,
    milestoneBrackets,
    onPlotPointerLeave,
    onPlotPointerMove,
    plotOverlayStyle,
    totalShares,
    unvestedSummary,
  } = view;
  const scrollPortRef = useRef<HTMLDivElement>(null);
  const [scrollPortWidth, setScrollPortWidth] = useState(0);

  useEffect(() => {
    const node = scrollPortRef.current;
    if (!node) return undefined;

    const syncWidth = () => setScrollPortWidth(node.clientWidth);
    syncWidth();
    const resizeObserver = new ResizeObserver(syncWidth);
    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, []);

  const needsHorizontalScroll = scrollPortWidth > 0 && chartMinWidth > scrollPortWidth;

  return (
    <div
      className={needsHorizontalScroll ? 'overflow-x-auto' : 'overflow-x-visible'}
      ref={scrollPortRef}
    >
      <div
        style={{
          minWidth: needsHorizontalScroll
            ? chartMinWidth + VESTING_GRAPH_EVENT_BADGE_OVERFLOW_PX
            : chartMinWidth,
        }}
      >
        <div
          className={twMerge('relative', !isPlotInteractive && 'pointer-events-none')}
          onPointerLeave={onPlotPointerLeave}
          onPointerMove={onPlotPointerMove}
          style={needsHorizontalScroll ? { width: chartMinWidth } : undefined}
        >
          <div className="pointer-events-none absolute z-[1]" style={plotOverlayStyle}>
            {milestoneBrackets.map((bracket) => (
              <VestingGraphTargetMilestoneColumn
                key={`column-${bracket.label}`}
                leftPercent={bracket.leftPercent}
                widthPercent={bracket.widthPercent}
                yPercent={bracket.yPercent}
              />
            ))}
            <VestingGraphPlotMarkers layer="column" />
          </div>

          <VestingGraphChart />

          <div className="pointer-events-none absolute z-[3]" style={plotOverlayStyle}>
            {milestoneBrackets.map((bracket) => (
              <VestingGraphTargetMilestoneColumnEdges
                key={`milestone-edges-${bracket.label}`}
                leftPercent={bracket.leftPercent}
                widthPercent={bracket.widthPercent}
                yPercent={bracket.yPercent}
              />
            ))}
            {milestoneBrackets.map((bracket) => (
              <VestingGraphTargetMilestoneBracket
                description={bracket.description}
                key={bracket.label}
                label={bracket.label}
                leftPercent={bracket.leftPercent}
                widthPercent={bracket.widthPercent}
                yPercent={bracket.yPercent}
              />
            ))}
            {cliffOverlay && vestingGraph.cliff && (
              <VestingGraphCliffBracket
                label={<AppFormattedMessage id="CLIFF" />}
                leftPercent={cliffOverlay.leftPercent}
                tooltip={
                  <VestingGraphTooltip
                    active
                    cliff={vestingGraph.cliff}
                    cliffEndDateLabel={formatVestingCalendarDate(
                      format,
                      vestingGraph.cliff.endDate,
                    )}
                    totalShares={totalShares}
                    unvestedSummary={unvestedSummary}
                    variant="cliff"
                  />
                }
                widthPercent={cliffOverlay.widthPercent}
                yPercent={Number(toRound(cliffOverlay.cumulativePercentAtCliff))}
              />
            )}
            <VestingGraphPlotMarkers layer="badge" />
          </div>

          <VestingGraphAxisTicks
            axisTicks={axisTicks}
            format={format}
            hoveredPeriod={hoveredPeriod}
            introDelayForReveal={introDelayForReveal}
            introPhase={introPhase}
          />
        </div>
      </div>
    </div>
  );
};
